import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  getProjectsPageByUserId,
  mapProjectToCard,
} from "@/services/projects/projectService";
import { sortByRecency } from "@/utils/recencySort";
import { deduplicateMergeById } from "@/utils/deduplicateById";
import { PROJECT_DELETED_EVENT } from "@/utils/dataSyncEvents";
import { PROJECTS_PAGE_SIZE } from "@/utils/paginationConstants";

/**
 * Lista paginada de projetos para /projects (updatedAt DESC).
 */
export function useProjectsPage() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);
  /** @type {React.MutableRefObject<import("@/services/firebase/dates").PaginationCursor | null>} */
  const cursorRef = useRef(null);
  const loadMoreInFlightRef = useRef(false);

  const resetPaging = useCallback(() => {
    setProjects([]);
    setHasMore(false);
    setLoadMoreError(null);
    cursorRef.current = null;
    loadMoreInFlightRef.current = false;
  }, []);

  const loadFirstPage = useCallback(async () => {
    if (!user?.uid) {
      resetPaging();
      setLoadingInitial(false);
      return;
    }

    setLoadingInitial(true);
    setError(null);
    setLoadMoreError(null);
    cursorRef.current = null;

    try {
      const result = await getProjectsPageByUserId(user.uid, {
        limitCount: PROJECTS_PAGE_SIZE,
      });

      setProjects(result.items);
      cursorRef.current = result.cursor;
      setHasMore(result.hasMore);
    } catch (err) {
      setError(err);
      resetPaging();
    } finally {
      setLoadingInitial(false);
    }
  }, [user?.uid, resetPaging]);

  const loadMore = useCallback(async () => {
    if (
      !user?.uid ||
      !hasMore ||
      loadMoreInFlightRef.current ||
      loadingInitial ||
      !cursorRef.current
    ) {
      return;
    }

    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    setLoadMoreError(null);

    try {
      const result = await getProjectsPageByUserId(user.uid, {
        limitCount: PROJECTS_PAGE_SIZE,
        cursor: cursorRef.current,
      });

      setProjects((current) => deduplicateMergeById(current, result.items));
      cursorRef.current = result.cursor;
      setHasMore(result.hasMore);
    } catch (err) {
      setLoadMoreError(err);
    } finally {
      loadMoreInFlightRef.current = false;
      setLoadingMore(false);
    }
  }, [user?.uid, hasMore, loadingInitial]);

  const addProject = useCallback((project) => {
    setProjects((current) =>
      sortByRecency([
        {
          ...project,
          updatedAt: project.updatedAt ?? new Date(),
        },
        ...current.filter((item) => item.id !== project.id),
      ]),
    );
  }, []);

  const removeProject = useCallback((projectId) => {
    setProjects((current) => current.filter((project) => project.id !== projectId));
  }, []);

  const patchProject = useCallback((projectId, updates) => {
    setProjects((current) => {
      const existing = current.find((project) => project.id === projectId);

      if (!existing) {
        return current;
      }

      const updated = {
        ...existing,
        ...updates,
        updatedAt: updates.updatedAt ?? new Date(),
      };

      return sortByRecency([
        updated,
        ...current.filter((project) => project.id !== projectId),
      ]);
    });
  }, []);

  useEffect(() => {
    const handleProjectDeleted = (event) => {
      const { projectId } = event.detail ?? {};

      if (!projectId) {
        return;
      }

      removeProject(projectId);
    };

    window.addEventListener(PROJECT_DELETED_EVENT, handleProjectDeleted);

    return () => {
      window.removeEventListener(PROJECT_DELETED_EVENT, handleProjectDeleted);
    };
  }, [removeProject]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user?.uid) {
        if (!cancelled) {
          resetPaging();
          setLoadingInitial(false);
        }
        return;
      }

      if (!cancelled) {
        setLoadingInitial(true);
        setError(null);
        setLoadMoreError(null);
        cursorRef.current = null;
      }

      try {
        const result = await getProjectsPageByUserId(user.uid, {
          limitCount: PROJECTS_PAGE_SIZE,
        });

        if (!cancelled) {
          setProjects(result.items);
          cursorRef.current = result.cursor;
          setHasMore(result.hasMore);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          resetPaging();
        }
      } finally {
        if (!cancelled) {
          setLoadingInitial(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, resetPaging]);

  const cardProjects = useMemo(
    () => projects.map(mapProjectToCard),
    [projects],
  );

  return {
    projects,
    cardProjects,
    loadingInitial,
    loadingMore,
    hasMore,
    error,
    loadMoreError,
    loadMore,
    reloadFirstPage: loadFirstPage,
    addProject,
    removeProject,
    patchProject,
  };
}
